import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Carousel.css';

const Carousel = ({ slides = [] }) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isPlaying, setIsPlaying] = useState(true);
  const autoPlayTimerRef = useRef(null);
  const touchStartXRef = useRef(0);
  const touchEndXRef = useRef(0);
  const carouselRef = useRef(null);

  const AUTO_PLAY_INTERVAL = 5000;
  const SWIPE_THRESHOLD = 50;

  const goToSlide = useCallback((slideIndex) => {
    if (slideIndex < 0 || slideIndex >= slides.length) return;
    setCurrentSlide(slideIndex);
  }, [slides.length]);

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % slides.length);
  }, [currentSlide, slides.length, goToSlide]);

  const previousSlide = useCallback(() => {
    goToSlide((currentSlide - 1 + slides.length) % slides.length);
  }, [currentSlide, slides.length, goToSlide]);

  const startAutoPlay = useCallback(() => {
    if (isPlaying) return;
    setIsPlaying(true);
    autoPlayTimerRef.current = setInterval(() => {
      nextSlide();
    }, AUTO_PLAY_INTERVAL);
  }, [isPlaying, nextSlide]);

  const pauseAutoPlay = useCallback(() => {
    if (autoPlayTimerRef.current) {
      clearInterval(autoPlayTimerRef.current);
      autoPlayTimerRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  const resumeAutoPlay = useCallback(() => {
    if (!isPlaying) {
      startAutoPlay();
    }
  }, [isPlaying, startAutoPlay]);

  // Auto-play effect
  useEffect(() => {
    if (isPlaying) {
      autoPlayTimerRef.current = setInterval(() => {
        nextSlide();
      }, AUTO_PLAY_INTERVAL);
    }

    return () => {
      if (autoPlayTimerRef.current) {
        clearInterval(autoPlayTimerRef.current);
      }
    };
  }, [isPlaying, nextSlide]);

  // Pause on visibility change
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        pauseAutoPlay();
      } else {
        resumeAutoPlay();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [pauseAutoPlay, resumeAutoPlay]);

  // Touch handlers
  const handleTouchStart = (e) => {
    touchStartXRef.current = e.touches[0].clientX;
  };

  const handleTouchEnd = (e) => {
    touchEndXRef.current = e.changedTouches[0].clientX;
    const diff = touchStartXRef.current - touchEndXRef.current;

    if (Math.abs(diff) < SWIPE_THRESHOLD) return;

    if (diff > 0) {
      nextSlide();
    } else {
      previousSlide();
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!carouselRef.current?.contains(document.activeElement)) return;

      switch (e.key) {
        case 'ArrowLeft':
          e.preventDefault();
          previousSlide();
          break;
        case 'ArrowRight':
          e.preventDefault();
          nextSlide();
          break;
        case 'Home':
          e.preventDefault();
          goToSlide(0);
          break;
        case 'End':
          e.preventDefault();
          goToSlide(slides.length - 1);
          break;
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [nextSlide, previousSlide, goToSlide, slides.length]);

  if (slides.length === 0) return null;

  return (
    <section className="hero" aria-label="Featured content">
      <div
        className="carousel"
        id="carousel"
        role="region"
        aria-label="Carousel of featured content"
        ref={carouselRef}
        onMouseEnter={pauseAutoPlay}
        onMouseLeave={resumeAutoPlay}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
      >
        <div className="carousel-track" id="carousel-track">
          {slides.map((slide, index) => (
            <div
              key={index}
              className={`carousel-slide ${index === currentSlide ? 'carousel-slide--active' : ''}`}
              style={{ backgroundImage: `url(${slide.backgroundImage})` }}
              data-slide={index + 1}
              aria-hidden={index !== currentSlide}
              tabIndex={index === currentSlide ? 0 : -1}
            >
              <div className="carousel-content">
                {slide.content}
              </div>
            </div>
          ))}
        </div>

        {/* Carousel controls */}
        <div className="carousel-controls">
          <button
            className="carousel-btn carousel-btn--prev"
            aria-label="Previous slide"
            onClick={previousSlide}
          >
            ‹
          </button>
          <button
            className="carousel-btn carousel-btn--next"
            aria-label="Next slide"
            onClick={nextSlide}
          >
            ›
          </button>
        </div>

        {/* Carousel indicators */}
        <div className="carousel-indicators">
          {slides.map((_, index) => (
            <button
              key={index}
              className={`carousel-indicator ${index === currentSlide ? 'carousel-indicator--active' : ''}`}
              aria-label={`Go to slide ${index + 1}`}
              data-slide={index + 1}
              onClick={() => goToSlide(index)}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default Carousel;

